import React, { useState } from "react";
import { api, BACKEND_URL, euro, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Printer, Send } from "lucide-react";

const tid = (s) => String(s).replace(/[^a-zA-Z0-9_-]/g, "");

function EmailDialog({ kind, refId, open, onOpenChange }) {
  const [info, setInfo] = useState(null), [to, setTo] = useState(""), [busy, setBusy] = useState(false);
  const load = () => api.get(`/receipts/${kind}/${encodeURIComponent(refId)}/info`).then((r) => { setInfo(r.data); setTo(r.data.customer_email || ""); }).catch(() => setInfo({ available: false, reason: "Ricevuta non disponibile" }));
  const send = async () => {
    setBusy(true);
    try { await api.post(`/receipts/${kind}/${encodeURIComponent(refId)}/email`, { email: to.trim() }); toast.success(`Ricevuta inviata a ${to.trim()}`); onOpenChange(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (v) { load(); } }}>
      <DialogContent className="max-w-md" data-testid={`receipt-email-dialog-${tid(refId)}`} onOpenAutoFocus={load}>
        <DialogHeader><DialogTitle>Invia la ricevuta al cliente</DialogTitle>
          <DialogDescription>Il cliente riceve un'email con il link al PDF: prezzo al pubblico in grande e, più in piccolo, il prezzo pagato da te.</DialogDescription></DialogHeader>
        {!info ? <p className="text-sm text-muted-foreground">Caricamento…</p> : !info.available ? <p className="text-sm text-amber-600" data-testid="receipt-unavailable">{info.reason}</p> : <div className="space-y-3">
          <p className="text-sm"><span className="text-muted-foreground">{info.number} · {info.product}</span><br /><strong className="text-lg" data-testid="receipt-public-price">{info.public_price != null ? euro(info.public_price) : "Prezzo al pubblico non disponibile"}</strong></p>
          <div><Label className="text-xs">Email del cliente</Label><Input type="email" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} placeholder="cliente@email.it" data-testid="receipt-email-input" /></div>
          {info.sent?.length > 0 && <p className="text-xs text-muted-foreground" data-testid="receipt-sent-list">Già inviata a: {info.sent.map((x) => x.to).join(", ")}</p>}
        </div>}
        <DialogFooter><Button disabled={busy || !info?.available || !/^\S+@\S+\.\S+$/.test(to.trim())} onClick={send} className="gap-1.5" data-testid="receipt-email-send"><Send className="w-4 h-4" />{busy ? "Invio…" : "Invia ricevuta"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PurchaseReceipt({ kind = "order", refId, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!refId) return null;
  const id = tid(refId);
  return (
    <span className="inline-flex items-center gap-1.5">
      <Button asChild size="sm" variant="outline" className={compact ? "h-8 w-8 p-0" : "gap-1.5"} title="Ricevuta PDF da stampare"><a href={`${BACKEND_URL}/api/receipts/${kind}/${encodeURIComponent(refId)}`} target="_blank" rel="noopener noreferrer" data-testid={`receipt-pdf-${id}`}><Printer className="w-4 h-4" />{!compact && "Ricevuta PDF"}</a></Button>
      <Button size="sm" variant="outline" className={compact ? "h-8 w-8 p-0" : "gap-1.5"} title="Invia la ricevuta al cliente" onClick={() => setOpen(true)} data-testid={`receipt-email-${id}`}><Mail className="w-4 h-4" />{!compact && "Invia al cliente"}</Button>
      <EmailDialog kind={kind} refId={refId} open={open} onOpenChange={setOpen} />
    </span>
  );
}
